import { useRef } from 'react';

// Bot signals for the contact forms, checked by the contact-form Lambda
// (lambda/contact-form/guard.mjs):
//   - an `hp_field` input hidden from people; form-filling bots fill every input
//   - how long the form was open before Send
//
// Usage: render <BotTrapField inputRef={trap.inputRef} /> inside the <form>,
// and spread trap.signals() into the submit payload. Call trap.reset() after a
// successful send so the next submission is timed from then.
export function useFormBotTrap() {
  const inputRef = useRef<HTMLInputElement>(null);
  // performance.now() never jumps backward, unlike the wall clock, which can
  // move on a time sync or a laptop waking up and make a person look instant.
  const openedAt = useRef(performance.now());

  return {
    inputRef,
    // Read from the input itself rather than React state, so a bot that sets
    // the value without firing input events is still caught.
    signals: () => ({
      hp_field: inputRef.current?.value ?? '',
      elapsed_ms: Math.round(performance.now() - openedAt.current),
    }),
    reset: () => {
      if (inputRef.current) inputRef.current.value = '';
      openedAt.current = performance.now();
    },
  };
}

// Moved off-screen rather than display:none, which some bots skip. Hidden
// from screen readers and skipped by Tab. The name means nothing to browser
// or password-manager autofill, and the data-* attributes opt out of the
// common password managers, so a person never fills it by accident.
export function BotTrapField({ inputRef }: { inputRef: React.RefObject<HTMLInputElement> }) {
  return (
    <div aria-hidden="true" style={{ position: 'absolute', left: '-10000px', top: 'auto', width: 1, height: 1, overflow: 'hidden' }}>
      <label htmlFor="hp_field">Leave this field empty</label>
      <input
        ref={inputRef}
        id="hp_field"
        name="hp_field"
        type="text"
        tabIndex={-1}
        autoComplete="off"
        defaultValue=""
        data-1p-ignore=""
        data-lpignore="true"
        data-bwignore=""
        data-form-type="other"
      />
    </div>
  );
}
